import { z } from "zod";

// BPA Form Validation Schema
export const BPAFormSchema = z.object({
  name: z.string().min(1, "Name is required").max(255, "Name too long"),
  code: z
    .string()
    .min(1, "Code is required")
    .max(50, "Code too long")
    .regex(/^[A-Z0-9_-]+$/i, "Code must contain only letters, numbers, hyphens, and underscores"),
  slug: z
    .string()
    .min(1, "Slug is required")
    .max(255, "Slug too long")
    .regex(/^[A-Z0-9_-]+$/i, "Slug must contain only letters, numbers, hyphens, and underscores"),
  parentId: z.string().uuid().optional().nullable(),
  displayOrder: z.number().int().min(0),
});

export type BPAFormData = z.infer<typeof BPAFormSchema>;

// Edition Form Validation Schemas
export const EditionFormSchema = z
  .object({
    name: z.string().min(2, "Name must be at least 2 characters").max(255, "Name too long").trim(),
    code: z.string().min(2, "Code must be at least 2 characters").max(100, "Code too long").trim(),
    directoryId: z
      .string({ message: "Directory is required" })
      .uuid({ message: "Directory is required" }),
    status: z.enum(["draft", "published", "archived"]),
    publishedDate: z
      .union([z.string(), z.null()])
      .optional()
      .transform(val => (val === "" ? null : val)),
  })
  .refine(
    data => {
      if (data.status === "published" && !data.publishedDate) {
        return false;
      }
      return true;
    },
    {
      message: "Published date is required when status is published",
      path: ["publishedDate"],
    },
  );

export type EditionFormData = z.infer<typeof EditionFormSchema>;

// Region Form Validation Schemas
export const RegionFormSchema = z.object({
  name: z.string().min(1, "Name is required").max(255, "Name too long"),
  code: z
    .string()
    .min(1, "Code is required")
    .max(50, "Code too long")
    .regex(/^[A-Z0-9_-]+$/i, "Code must contain only letters, numbers, hyphens, and underscores"),
  parentId: z.string().uuid().optional().nullable(),
  displayOrder: z.number().int().min(0),
});

export type RegionFormData = z.infer<typeof RegionFormSchema>;

// Miscellaneous Form Validation Schema
export const MiscellaneousFormSchema = z.object({
  name: z.string().min(1, "Name is required").max(255, "Name too long"),
  code: z
    .string()
    .min(1, "Code is required")
    .max(50, "Code too long")
    .regex(/^[A-Z0-9_-]+$/i, "Code must contain only letters, numbers, hyphens, and underscores"),
  parentId: z.string().uuid().optional().nullable(),
  displayOrder: z.number().int().min(0),
});

export type MiscellaneousFormData = z.infer<typeof MiscellaneousFormSchema>;

// Directory Form Validation Schema
export const DirectoryFormSchema = z.object({
  name: z
    .string()
    .min(2, "Name must be at least 2 characters")
    .max(255, "Name cannot exceed 255 characters"),
  code: z
    .string()
    .min(2, "Code must be at least 2 characters")
    .max(100, "Code cannot exceed 100 characters"),
  parentId: z.string().uuid().optional().nullable(),
  displayOrder: z.number().int().min(0),
});

export type DirectoryFormData = z.infer<typeof DirectoryFormSchema>;
